import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  IconBell,
  IconBellFilled,
  IconChevronDown,
  IconLoader2,
} from '@tabler/icons-react';

import {
  ALERT_BLUE,
  ALERT_BLUE_BG,
  ChannelIcon,
  useScoreDropAlerts,
  type UseScoreDropAlertsArgs,
} from './useScoreDropAlerts';

const POPOVER_WIDTH = 320;

/** Small on/off switch visual (WK blue when on). */
const SwitchVisual = ({ on }: { on: boolean }) => (
  <span
    className="relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors"
    style={{ backgroundColor: on ? ALERT_BLUE : '#cbd5e1' }}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
        on ? 'translate-x-4' : 'translate-x-0.5'
      }`}
    />
  </span>
);

interface Props extends UseScoreDropAlertsArgs {
  compact?: boolean;
}

/**
 * Option A — a single state-aware bell button that opens a popover with one
 * switch per channel. The "Watch"/"Notify me" pattern (GitHub, Jira, ADO):
 * recognisable entry point, the header states the trigger condition, and the
 * switches give per-channel control without crowding the action bar.
 */
const SubscribeBellPopover = ({ compact = false, ...args }: Props) => {
  const {
    channels,
    activeChannels,
    isSubscribed,
    busy,
    pendingChannel,
    ready,
    toggleChannel,
    applicationName,
    environment,
  } = useScoreDropAlerts(args);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (!r) return;
      setPos({
        left: Math.max(8, r.right - POPOVER_WIDTH),
        top: r.bottom + 6,
      });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!ready) return null;

  const count = activeChannels.length;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Manage score-drop alerts"
        className="inline-flex h-8 items-center gap-1.5 rounded-[2px] border px-3 text-sm font-medium transition-colors"
        style={
          isSubscribed
            ? { borderColor: ALERT_BLUE, backgroundColor: ALERT_BLUE_BG, color: ALERT_BLUE }
            : { borderColor: '#d1d5db', backgroundColor: '#fff', color: '#4b5563' }
        }
      >
        {isSubscribed ? <IconBellFilled size={16} /> : <IconBell size={16} />}
        {!compact && <span>{isSubscribed ? 'Alerts on' : 'Notify me'}</span>}
        {isSubscribed && count > 0 && (
          <span
            className="ml-0.5 inline-flex min-w-4 items-center justify-center rounded-full px-1 text-[11px] font-semibold text-white"
            style={{ backgroundColor: ALERT_BLUE }}
          >
            {count}
          </span>
        )}
        <IconChevronDown size={14} className="opacity-60" />
      </button>

      {open && pos
        ? createPortal(
            <>
              <div
                className="fixed inset-0 z-[99998]"
                onClick={() => setOpen(false)}
              />
              <div
                role="dialog"
                aria-label="Score-drop alert settings"
                className="fixed z-[99999] overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg"
                style={{ left: pos.left, top: pos.top, width: POPOVER_WIDTH }}
              >
                <div className="border-b border-gray-100 px-4 py-3">
                  <p className="text-sm font-semibold text-gray-800">
                    Alert me when the score drops below 100%
                  </p>
                  <p className="mt-0.5 truncate text-xs text-gray-500">
                    {applicationName} · {environment}
                  </p>
                </div>

                <ul className="px-1.5 py-1.5">
                  {channels.map((ch) => {
                    const on = activeChannels.includes(ch.key);
                    const rowBusy = pendingChannel === ch.key;
                    return (
                      <li key={ch.key}>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => toggleChannel(ch.key, ch.label)}
                          className="flex w-full items-center gap-3 rounded px-2.5 py-2 text-left transition-colors hover:bg-gray-50 disabled:opacity-60"
                        >
                          <span style={{ color: on ? ALERT_BLUE : '#6b7280' }}>
                            <ChannelIcon channelKey={ch.key} size={18} />
                          </span>
                          <span className="flex-1 text-sm text-gray-700">
                            {ch.label}
                          </span>
                          {rowBusy ? (
                            <IconLoader2
                              size={16}
                              className="animate-spin text-gray-400"
                            />
                          ) : (
                            <SwitchVisual on={on} />
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>

                <div className="border-t border-gray-100 px-4 py-2">
                  <p className="text-xs text-gray-400">
                    {isSubscribed
                      ? 'You’ll be notified on the channels above.'
                      : 'Pick a channel to start receiving alerts.'}
                  </p>
                </div>
              </div>
            </>,
            document.body,
          )
        : null}
    </>
  );
};

export default SubscribeBellPopover;
